import { PageContextManager } from './pageContextManager';

export interface VehicleYmmt {
  year?: string | number;
  make?: string;
  model?: string;
  trim?: string;
}

const isNonEmpty = (v: any) =>
  v !== undefined && v !== null && String(v).trim().length > 0;

/**
 * Returns true if the visitor currently has any vehicle in context:
 * - A topcarid value on the page
 * - Schema.org Product/Vehicle JSON on the page (VDP)
 * - Stored interest data with vehicle make/model/year
 */
export const hasVehicleContext = (storedInterestVehicle?: any): boolean => {
  try {
    if (PageContextManager.getTopCarId()) return true;
  } catch { /* ignore */ }

  try {
    if (PageContextManager.getVehicleSchemaData()) return true;
  } catch { /* ignore */ }

  if (
    storedInterestVehicle &&
    (isNonEmpty(storedInterestVehicle.make) ||
      isNonEmpty(storedInterestVehicle.model) ||
      isNonEmpty(storedInterestVehicle.year))
  ) {
    return true;
  }

  return false;
};

/**
 * Best-effort vehicle Year/Make/Model/Trim from stored interest first,
 * then from schema.org Product/Vehicle `name` on the page.
 */
export const getVehicleYmmt = (storedInterestVehicle?: any): VehicleYmmt | null => {
  if (storedInterestVehicle) {
    const v = storedInterestVehicle;
    if (isNonEmpty(v.year) || isNonEmpty(v.make) || isNonEmpty(v.model)) {
      return {
        year: v.year,
        make: v.make,
        model: v.model,
        trim: v.version || v.trim,
      };
    }
  }

  try {
    const schema = PageContextManager.getVehicleSchemaData();
    if (schema?.name) {
      // schema.name is typically "2024 Honda Civic LX" or similar
      const name = String(schema.name).trim();
      const match = /^(\d{4})\s+(\S+)\s+(\S+)(?:\s+(.+))?$/.exec(name);
      if (match) {
        return {
          year: match[1],
          make: match[2],
          model: match[3],
          trim: match[4],
        };
      }
      // Fall back: stuff the whole name into model so caller can still display it
      return { model: name };
    }
  } catch { /* ignore */ }

  return null;
};

/**
 * Build the localized "Make an offer on …" form title.
 * Falls back to a generic title when no vehicle info is available.
 */
export const buildOfferFormTitle = (
  language: 'en' | 'fr',
  vehicle?: VehicleYmmt | null
): string => {
  const parts = [vehicle?.year, vehicle?.make, vehicle?.model, vehicle?.trim]
    .filter((p) => isNonEmpty(p))
    .map((p) => String(p).trim());

  const description = parts.join(' ').trim();

  if (!description) {
    return language === 'fr' ? 'Faites une offre' : 'Make an offer';
  }

  return language === 'fr'
    ? `Faites une offre sur la ${description}`
    : `Make an offer on a ${description}`;
};
